import React from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from 'src/reducers';

import { _config } from 'Utilities/envConfig';
import i18n from 'Utilities/i18n';

import Consent from 'Containers/Consent';
import Home from 'Containers/Home';
import InboundPartner from 'Containers/InboundPartner';
import Invitation from 'Containers/Invitation';
import LibreLinkUp from 'Containers/LibreLinkUp';
import LibreView from 'Containers/LibreView';
import MainContainer from 'Containers/MainContainer';
import OtherConnections from 'Containers/OtherConnections';
import Partner from 'Containers/Partner';
import QuickShare from 'Containers/QuickShare';

import ConfigVersionMessage from 'Components/utility/ConfigVersionMessage';
import ErrorBoundary from 'Components/utility/ErrorBoundary';
import UnexpectedError from 'Components/utility/UnexpectedError';

const mapState = ({ auth: { loggedIn }, nav: { location }, user }: RootState) => {
  return {
    loggedIn: loggedIn && user.id,
    onApp: !!location.match(/^\/share\//),
    onLLU: !!location.match(/^\/librelinkup/),
    onLV: !!location.match(/^\/libreview/),
    onQuickShare: !!location.match(/^\/quickshare/),
    onOtherConnections: !!location.match(/^\/otherConnections/i),
    onConsent: !!location.match(/^\/consent/),
    onInvitation: !!location.match(/^\/invitation/),
    onInbound: !!location.match(/^\/inbound/),
    onRoot: !!location.match(/^\/?$/),
  };
};

const connector = connect(mapState);

type Props = ConnectedProps<typeof connector> & Record<string, any>;

const Main: React.FC<React.PropsWithChildren<Props>> = ({
  loggedIn,
  onApp,
  onLLU,
  onLV,
  onQuickShare,
  onOtherConnections,
  onConsent,
  onInvitation,
  onInbound,
  onRoot,
}) => {
  let content: React.ReactNode = null;

  if (!loggedIn) {
    if (_config.production) {
      return (
        <div className="d-flex flex-column w-100 bg-white p-5" style={{ height: '100vh' }}>
          <div className="text-xxl">{i18n.t('UnexpectedError.title')}</div>
          <div>{i18n.t('ErrorCodes.maintenanceTryLater')}</div>
        </div>
      );
    } else {
      return (
        <div
          className="d-flex flex-wrap align-items-start justify-content-between align-items-start bg-white p-5"
          style={{ height: '100vh' }}
        >
          <div className="mb-2">
            {_config.mock && (
              <div>
                Use <code>mockLogin()</code> for MOCK login
              </div>
            )}
            You are not logged in, please use <code>testHelper.login()</code> to login or set a
            token within <code>testHelper.setToken()</code>
          </div>
          <div className="d-flex">
            <ConfigVersionMessage />
          </div>
        </div>
      );
    }
  }

  let key = '';

  if (onRoot) {
    key = 'home';
    content = <Home />;
  } else if (onLV) {
    key = 'libreview';
    content = <LibreView />;
  } else if (onLLU) {
    key = 'librelinkup';
    content = <LibreLinkUp />;
  } else if (onQuickShare) {
    key = 'quickshare';
    content = <QuickShare />;
  } else if (onOtherConnections) {
    key = 'otherConnections';
    content = <OtherConnections />;
  } else if (onConsent) {
    key = 'consent';
    content = <Consent />;
  } else if (onInvitation) {
    key = 'invitation';
    content = <Invitation />;
  } else if (onInbound) {
    key = 'inbound';
    content = <InboundPartner />;
  } else if (onApp) {
    key = 'partner';
    content = <Partner />;
  }

  return (
    <MainContainer>
      <ErrorBoundary key={key} FallbackComponent={UnexpectedError}>
        {content}
      </ErrorBoundary>
    </MainContainer>
  );
};

export default connector(Main);
